"use client"

import { Link } from "react-router-dom"
import * as React from "react"
import Logo from "./Logo"
import { Facebook, Instagram, Linkedin, ArrowUpRight } from "lucide-react"
import { Button } from "./button"
import { ContactModal } from "./contact-modal"

const Footer = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const currentYear = new Date().getFullYear()

  const handleNavClick = (id) => {
    const currentPath = window.location.pathname
    if (currentPath === '/privacy-policy' || currentPath === '/terms-conditions') {
      const pathWithoutHash = "/"
      window.location.href = `${pathWithoutHash}#${id}`
    } else {
      window.history.pushState("", document.title, window.location.pathname + window.location.search)
    }

    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="relative overflow-hidden bg-brand-navy text-white">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] lg:w-[400px] lg:h-[400px] rounded-full bg-brand-gold opacity-10 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] lg:w-[600px] lg:h-[600px] rounded-full bg-brand-gold opacity-10 blur-3xl" />

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-10 sm:py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
          {/* Logo Section */}
          <div className="space-y-6 sm:col-span-2 lg:col-span-1">
            <Logo variant="light" className="h-16 sm:h-[4.5rem] w-auto max-w-[240px]" />
            <p className="text-white/80 text-[13px] sm:text-[14px] font-[400] leading-[20px] sm:leading-[22px] font-outfit max-w-sm">
              EgtoGo is a leading ridesharing platform connecting riders and drivers for safe, affordable, and reliable
              transportation. Whether you're commuting to work, heading out with friends, or exploring new places,
              EgtoGo makes it seamless.
            </p>
            <div className="flex items-center gap-5 sm:gap-6">
              <a href="https://www.facebook.com/share/19f8ziRSbh/" target="_blank" rel="noopener noreferrer">
                <Facebook className="w-5 h-5 sm:w-6 sm:h-6 text-white/80 hover:text-brand-gold transition-colors" />
              </a>
              <a href="https://www.instagram.com/egtogo?igsh=bmNkN3E4Nml1cXA4" target="_blank" rel="noopener noreferrer">
                <Instagram className="w-5 h-5 sm:w-6 sm:h-6 text-white/80 hover:text-brand-gold transition-colors" />
              </a>
              <a href="https://www.linkedin.com/company/106875815/admin/dashboard/" target="_blank" rel="noopener noreferrer">
                <Linkedin className="w-5 h-5 sm:w-6 sm:h-6 text-white/80 hover:text-brand-gold transition-colors" />
              </a>
              <a href="https://x.com/eg_go78464" target="_blank" rel="noopener noreferrer">
                <svg className="w-5 h-5 sm:w-[22px] sm:h-[22px] text-white/80 hover:text-brand-gold transition-colors" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.4907 1.73633H19.4741L12.9563 9.18575L20.6239 19.3227H14.6202L9.91788 13.1747L4.53734 19.3227H1.55216L8.52359 11.3547L1.16797 1.73633H7.32411L11.5746 7.35586L16.4907 1.73633ZM15.4436 17.537H17.0967L6.42585 3.42823H4.65188L15.4436 17.537Z" fill="currentColor"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[18px] sm:text-[20px] font-[600] leading-[25.2px] font-outfit mb-4 sm:mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <Link 
                  to="#home"
                  onClick={(e) => {
                    e.preventDefault()
                    handleNavClick('home')
                  }}
                  className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="#about"
                  onClick={(e) => {
                    e.preventDefault()
                    handleNavClick('about')
                  }}
                  className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="#how-it-works"
                  onClick={(e) => {
                    e.preventDefault()
                    handleNavClick('how-it-works')
                  }}
                  className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]"
                >
                  How It Works
                </Link>
              </li>
              <li>
                <Link
                  to="#usps"
                  onClick={(e) => {
                    e.preventDefault()
                    handleNavClick('usps')
                  }}
                  className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]"
                >
                  Our USPs
                </Link>
              </li>
              <li>
                <Link
                  to="#partner"
                  onClick={(e) => {
                    e.preventDefault()
                    handleNavClick('partner')
                  }}
                  className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]"
                >
                  Become partner
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-[18px] sm:text-[20px] font-[600] leading-[25.2px] font-outfit mb-4 sm:mb-6 text-white">Legal</h3>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <Link to="/terms-conditions" onClick={scrollToTop} className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" onClick={scrollToTop} className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-[18px] sm:text-[20px] font-[600] leading-[25.2px] font-outfit mb-4 sm:mb-6 text-white">Contact Info</h3>
            <ul className="space-y-3 sm:space-y-4">
              <li className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[20px] sm:leading-[17.64px] break-words">
                <span className="block">Email: support@egtogo.com</span>
                <span className="block mt-1">
                  <a href="mailto:egtogoservices@gmail.com" className="hover:text-brand-gold">
                    egtogoservices@gmail.com
                  </a>
                </span>
              </li>
              <li className="text-white/80 text-[13px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]">Phone: +91 9955210717</li>
              <li className="pt-3 sm:pt-4">
                <Button
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-brand-gold text-brand-navy font-semibold rounded-2xl hover:bg-brand-gold-dark transition-colors text-sm sm:text-base"
                >
                  Contact Us
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 border-t-[0.89px] mt-8 sm:mt-10 lg:mt-12 pt-6 sm:pt-8 text-center text-white/70 text-[12px] sm:text-[14px] font-outfit font-[400] leading-[17.64px]">
          All Rights Reserved. EgtoGo by Eg To Go Services Private Limited © {currentYear}
        </div>
        <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </div>
    </footer>
  )  
}

export default Footer


// TODO: Add the footer background image
//  <p className="flex justify-center items-center absolute mb-12">
//         <Image src={Ellipse} alt="footer_bg" width={798} height={20} className="relative bottom-[56rem] left-[18rem]" />
//     </p> 
